/**
 * Compare surfaces (spec 2026-10-16-compare-surfaces-merge §3, §4.2, §12):
 * one dialog for "Check as-built against design" and "Compare surfaces /
 * cut & fill". The user picks what to compare against (a design or an
 * earlier survey), the two inputs, and which results to make — Cut & fill
 * volumes (surface-difference engine), Deviation map (deviation engine), or
 * both.
 *
 * Every decision lives in the pure model modules under
 * `@/lib/analyses/compare/` — availability, the deviation couplings, the
 * schema and its defaults. This file only renders them and runs them from
 * effects on watched values (`useWatch` / `useFormState`: React Compiler, no
 * ref reads during render).
 *
 * Body order (CLAUDE.md): Inputs → "What do you want?" → Settings → Add-ons
 * → Output → Advanced. With two results, "Starts 2 server jobs" sits in the
 * shell's sticky footer, next to "Run 2 analyses".
 *
 * Validation: the schema raises TAGS; `useCompareValidationText` is the one
 * tag→text map, provided through `FormMessageText` so every `FormMessage`
 * under the form prints text. An invalid submit moves focus to the first
 * field to fix (or the first result card when none is ticked).
 *
 * Submit (`useCompareSubmit`): what is sent is re-derived from the gates and
 * flags at that moment — a result or add-on that is locked, hidden or
 * disabled is never sent, whatever the form still holds; a cut & fill add-on
 * without cut & fill is stripped (ruling R20).
 */
import { zodResolver } from '@hookform/resolvers/zod';
import { useAtomValue } from 'jotai';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useForm, useFormState, useWatch, type FieldErrors, type Resolver, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { alignmentsAtom } from '@/atoms/alignment.atom';
import { COMPARE_PART_LABEL_KEYS } from '@/atoms/deviation-progress.atom';
import { layersAtom, type LayerData } from '@/atoms/layers.atom';
import { AnalysisDialogShell } from '@/components/analyses/AnalysisDialogShell';
import { AnalysisSection, FormInputRoleField, OutputSummary, SwapButton } from '@/components/analyses/sections';
import { CompareAddOns } from '@/components/compare/CompareAddOns';
import { CompareAdvanced } from '@/components/compare/CompareAdvanced';
import { CompareKindToggle } from '@/components/compare/CompareKindToggle';
import { CompareSettings } from '@/components/compare/CompareSettings';
import { OrientationSentence } from '@/components/compare/OrientationSentence';
import { ResultCards, type ResultLock } from '@/components/compare/ResultCards';
import { useCompareSubmit } from '@/components/compare/useCompareSubmit';
import { useCompareValidationText } from '@/components/compare/useCompareValidationText';
import { SurfacePicker } from '@/components/SurfacePicker';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormMessageText,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useFeature } from '@/hooks/useFeature';
import { useFeatureFlagEnabled } from '@/lib/analytics';
import {
  COMPARE_RESULTS,
  REASON_DEVIATION_NEEDS_DESIGN,
  REASON_NEEDS_POINTCLOUD_AS_BUILT,
  reconcileWithFallback,
  resultAvailability,
  roleSlots,
  type CompareKind,
  type CompareResult,
  type ResultsNote,
} from '@/lib/analyses/compare/availability';
import { deviationCouplingFor, type CouplingInput } from '@/lib/analyses/compare/deviationCoupling';
import {
  compareDefaults,
  compareSchema,
  defaultCompareName,
  type CompareFormValues,
  type ComparePatch,
  type PcMethod,
} from '@/lib/analyses/compare/formSchema';
import { submissionFor, type PayloadContext } from '@/lib/analyses/compare/payloads';
import { localizePreflightParams, preflightLines } from '@/lib/analyses/compare/preflight';
import { loadRemembered } from '@/lib/analyses/compare/rememberedSettings';
import { isPointcloudLayer, isVectorLayer } from '@/lib/analyses/candidates';
import { translateKey } from '@/lib/analyses/translateKey';
import {
  applyStationPatch,
  deriveStationVolumes,
  stationFormTarget,
  stationVolumesDefaults,
  type StationVolumesFormValues,
  type StationVolumesInitialParams,
} from '@/lib/stationVolumes/dialogModel';
import { cn } from '@/lib/utils';

export interface RunCompareSurfacesDialogProps {
  projectId: string;
  /** The A/B pool: every layer either engine can read as a surface. */
  candidateLayers: LayerData[];
  /** The right-clicked layer, seeded into its role (spec §4.1, ruling R16). */
  contextLayer?: LayerData;
  /** A re-run's or a retry's values; they win over remembered settings. Per
   *  group partial: a re-run restores only what its run stored (`rerun.ts`). */
  initialValues?: ComparePatch;
  /** A re-run's station-volume section (Volumes by chainage). */
  initialStationParams?: StationVolumesInitialParams;
  /** A one-line explanation of how this re-run differs from the original. */
  rerunNotice?: 'stationsMovedToCutFill' | 'expectationMovedToCutFill' | 'legacyMinSampling';
  /** Observes the form's values after every change (a copy). For embedding
   *  callers and tests; the dialog itself never needs it. */
  onValuesChange?: (values: CompareFormValues) => void;
}

/** What an "unticked" note says, per reason the result became unavailable. */
const UNTICKED_NOTE_KEYS: Record<string, string> = {
  [REASON_NEEDS_POINTCLOUD_AS_BUILT]: 'compareSurfaces.notes.untickedNeedsPointcloud',
  [REASON_DEVIATION_NEEDS_DESIGN]: 'compareSurfaces.notes.untickedNeedsDesign',
};

const PATCH_GROUPS: readonly string[] = ['inputs', 'cutFill', 'deviation', 'addOns', 'output'];

/** Writes a model patch into the form, field by field (so every `useWatch`
 *  on a single field hears it). A coupling's change is not the user's, so it
 *  does not mark the field dirty. */
function setPatch(form: UseFormReturn<CompareFormValues>, patch: ComparePatch) {
  const set = form.setValue as (name: string, value: unknown, options?: { shouldDirty?: boolean }) => void;
  for (const [key, value] of Object.entries(patch)) {
    if (value === undefined) continue;
    if (PATCH_GROUPS.includes(key)) {
      for (const [field, v] of Object.entries(value as Record<string, unknown>))
        set(`${key}.${field}`, v, { shouldDirty: false });
    } else {
      set(key, value, { shouldDirty: false });
    }
  }
}

const sameResults = (a: readonly CompareResult[], b: readonly CompareResult[]) =>
  a.length === b.length && a.every((r) => b.includes(r));

/**
 * PostHog flags per card (spec §6). Only an explicit `false` hides a card: an
 * unloaded flag (`undefined`) must not flash the dialog empty, and the
 * launcher already required one of them to open it. With both off — which the
 * launcher never allows — show both rather than an unusable dialog.
 */
function cardFlags(cutFill: boolean | undefined, deviation: boolean | undefined) {
  const flags = { cutFill: cutFill !== false, deviation: deviation !== false };
  return flags.cutFill || flags.deviation ? flags : { cutFill: true, deviation: true };
}

/** The first message in react-hook-form's error tree, in field order. */
function firstMessage(errors: unknown): string | null {
  if (!errors || typeof errors !== 'object') return null;
  const message = (errors as { message?: unknown }).message;
  if (typeof message === 'string' && message) return message;
  for (const [key, value] of Object.entries(errors)) {
    if (key === 'ref' || key === 'message' || key === 'type') continue;
    const found = firstMessage(value);
    if (found) return found;
  }
  return null;
}

/** Can focus reach it? Not disabled itself, nor inside a disabled fieldset. */
const focusable = (el: HTMLElement) => !el.matches(':disabled') && !el.closest('fieldset[disabled]');

export function RunCompareSurfacesDialog({
  projectId,
  candidateLayers,
  contextLayer,
  initialValues,
  initialStationParams,
  rerunNotice,
  onValuesChange,
}: RunCompareSurfacesDialogProps) {
  const { t, i18n } = useTranslation('project');
  const formId = useId();
  const allLayers = useAtomValue(layersAtom);
  const alignments = useAtomValue(alignmentsAtom);

  // Each card has its own gate (spec §6): a locked card is muted but still
  // clickable, and answers the click with the upgrade dialog.
  const cutFillGate = useFeature('analysis.surfaceDifference');
  const deviationGate = useFeature('analysis.deviation');
  const stationGate = useFeature('analysis.stationVolumes');
  const expectationGate = useFeature('analysis.surfaceExpectation');
  // `assertReportOptions` refuses a report below Pro and would fail the whole
  // start, so the box answers a locked click with the upgrade dialog.
  const reportGate = useFeature('report.pdf');
  const flags = cardFlags(useFeatureFlagEnabled('surfaceDifference'), useFeatureFlagEnabled('deviationAnalysis'));
  const locks: Record<CompareResult, ResultLock | null> = {
    cutFill: cutFillGate.allowed ? null : cutFillGate,
    deviation: deviationGate.allowed ? null : deviationGate,
  };

  const sorted = useMemo(() => [...candidateLayers].sort((a, b) => a.name.localeCompare(b.name)), [candidateLayers]);
  const findLayer = (id: string) =>
    id ? (allLayers.find((l) => l.id === id) ?? candidateLayers.find((l) => l.id === id)) : undefined;

  const [defaults] = useState<CompareFormValues>(() => {
    // A re-run's station section is seeded through `rerun`, with the live
    // alignments and the gate (formSchema's `baseValues` has neither).
    const rerun: ComparePatch | undefined =
      initialValues || initialStationParams
        ? {
            ...initialValues,
            ...(initialStationParams
              ? stationVolumesDefaults(initialStationParams, alignments, stationGate.allowed)
              : {}),
          }
        : undefined;
    const v = compareDefaults({ contextLayer, remembered: loadRemembered(projectId), rerun, language: i18n.language });
    // A preselected result that is hidden or locked starts unticked in favour
    // of one the user can actually run.
    const usable = (r: CompareResult) => flags[r] && !locks[r];
    let results = v.results.filter(usable);
    if (results.length === 0) {
      const fallback = COMPARE_RESULTS.find(usable) ?? COMPARE_RESULTS.find((r) => flags[r]);
      results = fallback ? [fallback] : [];
    }
    return { ...v, results };
  });

  const form = useForm<CompareFormValues>({
    // Typed on the schema's OUTPUT: its input type differs only by the shared
    // expectation fields' optionals, and the form only ever holds full values
    // (every default comes from `compareDefaults`). Without the cast the form
    // is inferred on the input type and nothing downstream type-checks — the
    // error class the two legacy dialogs carry in the tsc baseline.
    resolver: zodResolver(compareSchema) as unknown as Resolver<CompareFormValues>,
    defaultValues: defaults,
  });

  const [kind, results, topId, bottomId, topPcMethod, bottomPcMethod, deviationMode] = useWatch({
    control: form.control,
    name: [
      'kind',
      'results',
      'inputs.topLayerId',
      'inputs.bottomLayerId',
      'inputs.topPcMethod',
      'inputs.bottomPcMethod',
      'deviation.mode',
    ],
  });
  // Every value, for the couplings: they react to any change.
  const allValues = useWatch({ control: form.control });
  // Full values in practice: every default comes from `compareDefaults`.
  const values = allValues as CompareFormValues;
  const { dirtyFields, submitCount } = useFormState({ control: form.control });
  const dirtyMin = !!dirtyFields.deviation?.min;
  const dirtyMax = !!dirtyFields.deviation?.max;
  const dirtyTolerance = !!dirtyFields.deviation?.volumeTolerance;

  const design = findLayer(topId);
  const asBuilt = findLayer(bottomId);
  const availability = useMemo(
    () =>
      resultAvailability({
        kind,
        a: design,
        b: asBuilt,
        flags: { cutFill: flags.cutFill, deviation: flags.deviation },
      }),
    [kind, design, asBuilt, flags.cutFill, flags.deviation],
  );

  // ── Deviation couplings (reference re-pick, boundary clear, design-type
  //    defaults, horizontal-mode volumes). The model returns a minimal patch,
  //    empty once the form is consistent, so this settles.
  //    A re-run or retry that restores deviation settings (R24) starts from
  //    them as the "previous" state: the design-type defaults then fire only
  //    when the user changes the design, so the run's own mode, range,
  //    tolerance and reference mode survive the mount. Without restored
  //    deviation settings (a fresh open, or a cut & fill re-run) there is
  //    nothing to protect: prev stays null, and the design gets its defaults,
  //    including a mode its type allows, as before.
  const [initialCoupling] = useState<CouplingInput | null>(() =>
    initialValues?.deviation
      ? {
          values: structuredClone(defaults),
          candidates: sorted,
          layers: allLayers,
          dirty: { min: false, max: false, volumeTolerance: false },
        }
      : null,
  );
  const previousCoupling = useRef<CouplingInput | null>(initialCoupling);
  useEffect(() => {
    const next: CouplingInput = {
      // A deep copy: react-hook-form mutates its nested value objects in
      // place, so a kept reference would not remember the previous state.
      values: structuredClone(form.getValues()),
      candidates: sorted,
      layers: allLayers,
      dirty: { min: dirtyMin, max: dirtyMax, volumeTolerance: dirtyTolerance },
    };
    const patch = deviationCouplingFor(previousCoupling.current, next);
    previousCoupling.current = next;
    setPatch(form, patch);
  }, [allValues, sorted, allLayers, dirtyMin, dirtyMax, dirtyTolerance, form]);

  useEffect(() => {
    onValuesChange?.(structuredClone(form.getValues()));
  }, [allValues, onValuesChange, form]);

  // ── Results vs availability (spec §4.2, §12.8): a result that became
  //    unavailable is unticked with a note — with Cut & fill ticked in its
  //    place if nothing else is left — and ticked again by itself once it is
  //    available again, unless the user unticked it.
  const [autoUnticked, setAutoUnticked] = useState<CompareResult[]>([]);
  const [autoTicked, setAutoTicked] = useState<CompareResult[]>([]);
  const [resultsNote, setResultsNote] = useState<ResultsNote | null>(null);
  useEffect(() => {
    const next = reconcileWithFallback({ results, autoUnticked, autoTicked }, results, availability);
    if (!sameResults(next.results, results)) form.setValue('results', next.results);
    if (!sameResults(next.autoUnticked, autoUnticked)) setAutoUnticked(next.autoUnticked);
    if (!sameResults(next.autoTicked, autoTicked)) setAutoTicked(next.autoTicked);
    if (next.notes.length > 0) setResultsNote(next.notes[next.notes.length - 1]);
  }, [availability, results, autoUnticked, autoTicked, form]);

  // "Choose at least one result" is for the USER emptying the selection (or a
  // submit with none) — never for an automatic untick, which has its note.
  const [userEmptied, setUserEmptied] = useState(false);

  // ── On open, focus the first role picker that still needs a layer (§12.5).
  //    Runs before the dialog's own auto-focus, which leaves focus alone once
  //    it is inside the dialog.
  const roleARef = useRef<HTMLDivElement>(null);
  const roleBRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const { topLayerId, bottomLayerId } = form.getValues('inputs');
    const group = !topLayerId ? roleARef.current : !bottomLayerId ? roleBRef.current : null;
    group?.querySelector<HTMLElement>('[role="combobox"]')?.focus();
  }, [form]);

  const toggleResult = (result: CompareResult, checked: boolean) => {
    setResultsNote(null);
    const current = form.getValues('results');
    const next = COMPARE_RESULTS.filter((r) => (r === result ? checked : current.includes(r)));
    setUserEmptied(next.length === 0);
    form.setValue('results', next, { shouldDirty: true });
  };

  const swap = () => {
    // A copy: react-hook-form mutates `inputs` in place on each setValue.
    const i = { ...form.getValues('inputs') };
    const dirty = { shouldDirty: true };
    form.setValue('inputs.topLayerId', i.bottomLayerId, dirty);
    form.setValue('inputs.bottomLayerId', i.topLayerId, dirty);
    form.setValue('inputs.topSurfaceIds', i.bottomSurfaceIds, dirty);
    form.setValue('inputs.bottomSurfaceIds', i.topSurfaceIds, dirty);
    form.setValue('inputs.topPcMethod', i.bottomPcMethod, dirty);
    form.setValue('inputs.bottomPcMethod', i.topPcMethod, dirty);
  };

  // ── Compare mode vs a lone pick (B1). The as-built (B) is usually picked
  //    first; in Earlier-survey mode that scan is the LATER survey (A), so a
  //    lone B moves to A and focus goes to the earlier survey still to pick.
  //    Back in Design mode a lone point cloud in A is the as-built again.
  const [focusRole, setFocusRole] = useState<{ n: number; slot: 'a' | 'b' } | null>(null);
  useEffect(() => {
    if (!focusRole) return;
    const group = focusRole.slot === 'a' ? roleARef.current : roleBRef.current;
    group?.querySelector<HTMLElement>('[role="combobox"]')?.focus();
  }, [focusRole]);
  const changeKind = (next: CompareKind) => {
    const { topLayerId, bottomLayerId } = form.getValues('inputs');
    form.setValue('kind', next, { shouldDirty: true });
    const loneB = next === 'survey' && !topLayerId && !!bottomLayerId;
    const top = findLayer(topLayerId);
    const loneCloudA = next === 'design' && !bottomLayerId && !!top && isPointcloudLayer(top);
    if (!loneB && !loneCloudA) return;
    swap();
    // The role still to pick: the earlier survey, or the design.
    setFocusRole((prev) => ({ n: (prev?.n ?? 0) + 1, slot: loneB ? 'b' : 'a' }));
  };

  const boundaryCandidates = allLayers.filter((l) => isVectorLayer(l) && l.id !== topId && l.id !== bottomId);

  const [slotA, slotB] = roleSlots(kind);
  const roleALabel = translateKey(t, slotA.labelKey);
  const roleBLabel = translateKey(t, slotB.labelKey);

  // What the user will actually get: ticked, offered and not locked.
  const ticked = COMPARE_RESULTS.filter((r) => results.includes(r) && flags[r] && !locks[r]);
  const cutFillOn = ticked.includes('cutFill');
  const deviationOn = ticked.includes('deviation');
  // One card on offer and usable: nothing to choose (spec §6).
  const offered = COMPARE_RESULTS.filter((r) => availability[r].visible);
  // A lone card that is unavailable or locked is still shown: it carries the
  // reason, or the way to upgrade.
  const showChoice = offered.length > 1 || offered.some((r) => !availability[r].available || locks[r]);

  // ── Volumes by chainage (cut & fill only): one derivation feeds the
  //    section's messages, the Run button and the payload (D17).
  const stationValues: StationVolumesFormValues = values;
  const stationAlignment = alignments.get(values.alignmentKey);
  const stationDerived = deriveStationVolumes(stationAlignment, stationValues, values.cutFill.resolution);
  const applyStation = (patch: Partial<StationVolumesFormValues>) => applyStationPatch(stationFormTarget(form), patch);
  const stationsOn = cutFillOn && stationGate.allowed && values.stationVolumesEnabled;

  // ── The name: "A vs B", following the inputs until the user types in
  //    the field — and again once they leave it empty.
  const [nameEdited, setNameEdited] = useState(() => !!initialValues?.output?.name);
  const designName = design?.name;
  const asBuiltName = asBuilt?.name;
  useEffect(() => {
    if (nameEdited) return;
    // Half a pair ("vs Drone 14 Oct") is no name; wait for both.
    const name = designName && asBuiltName ? defaultCompareName({ name: designName }, { name: asBuiltName }) : '';
    if (form.getValues('output.name') !== name) form.setValue('output.name', name, { shouldDirty: false });
  }, [nameEdited, designName, asBuiltName, form]);
  const runName = values.output.name;

  // ── The PDF. A deviation-only run in horizontal mode has no statistics to
  //    print (they live in the volume pass horizontal turns off), so its PDF
  //    is disabled with its own reason (spec §4.2, C2). The tick itself is
  //    kept: leaving horizontal brings it back, and submit strips it.
  const pdfDisabledReason =
    deviationOn && !cutFillOn && deviationMode === 'horizontal' ? t('compareSurfaces.addOns.pdfHorizontal') : undefined;
  const pdfOn = reportGate.allowed && values.addOns.pdf && !pdfDisabledReason;
  const designIsCloud = !!design && isPointcloudLayer(design);
  // The renders: ticked, or forced by a report on a deviation run.
  const rendersOn = deviationOn && (values.deviation.renderPerSurface || pdfOn);

  /** Ticks a result from an add-on's "Add it" — or, locked, offers the
   *  upgrade and reports that nothing was ticked. */
  const addResult = (result: CompareResult): boolean => {
    const lock = locks[result];
    if (lock) {
      lock.promptUpgrade();
      return false;
    }
    toggleResult(result, true);
    return true;
  };

  // ── "Will create": exactly what will appear (spec §4.2), one line per
  //    result in plain words (C6) — gated items only when their gate is open
  //    AND they are ticked. The CRS caveat of a GeoTIFF is said once, below.
  const shownName = runName || '…';
  const named = { name: shownName, interpolation: { escapeValue: false } };
  const list = (items: (string | false)[]) => items.filter((i): i is string => !!i).join(', ');
  const cutFillItems = list([
    t('compareSurfaces.creates.bodies', named),
    t('compareSurfaces.creates.planView'),
    values.addOns.geotiff && t('compareSurfaces.creates.heatMap'),
    values.addOns.dxf && t('compareSurfaces.creates.dxf'),
    stationsOn && t('compareSurfaces.creates.stations'),
  ]);
  const deviationItems = list([
    t('compareSurfaces.creates.cloud', named),
    values.addOns.geotiff && t('compareSurfaces.creates.geotiff'),
    // A paired run's m³ are cut & fill's; only a deviation-only run shows its own.
    !cutFillOn && values.deviation.volumeStats && t('compareSurfaces.creates.volumeSummary'),
    // The RENDER child only runs for a GeoJSON or point-cloud design.
    rendersOn && (design?.type === 'VECTOR' || designIsCloud) && t('compareSurfaces.creates.renders'),
  ]);
  const creates = [
    cutFillOn && t('compareSurfaces.creates.cutFill', { items: cutFillItems, interpolation: { escapeValue: false } }),
    deviationOn &&
      t('compareSurfaces.creates.deviation', { items: deviationItems, interpolation: { escapeValue: false } }),
    pdfOn && (cutFillOn && deviationOn ? t('compareSurfaces.creates.pdfCombined') : t('compareSurfaces.creates.pdf')),
  ];
  const createsFootnote = values.addOns.geotiff && (cutFillOn || deviationOn) ? t('compareSurfaces.creates.crsNote') : null;

  // ── Preflight (spec §12.3), for what will run. The two-jobs line goes
  //    above the footer instead.
  const preflight = preflightLines(
    { ...values, results: ticked },
    allLayers,
    stationsOn ? stationAlignment : undefined,
  );
  const outputLines = preflight.filter((l) => l.key !== 'compareSurfaces.preflight.startsTwoJobs');
  const twoJobsLine = preflight.find((l) => l.key === 'compareSurfaces.preflight.startsTwoJobs');

  // ── Submit.
  const validationText = useCompareValidationText(kind);
  const mutation = useCompareSubmit({ projectId, layers: allLayers });
  const formRef = useRef<HTMLFormElement>(null);
  /** Bumped by an invalid submit; the effect below focuses after the errors render. */
  const [focusRequest, setFocusRequest] = useState<{ n: number; results: boolean; fallback: string | null }>({
    n: 0,
    results: false,
    fallback: null,
  });
  useEffect(() => {
    if (focusRequest.n === 0) return;
    const root = formRef.current;
    if (!root) return;
    // The first field to fix, in reading order: anything marked invalid that
    // focus can reach, plus the first usable result card when none is ticked.
    const candidates = Array.from(root.querySelectorAll<HTMLElement>('[aria-invalid="true"]')).filter(focusable);
    if (focusRequest.results) {
      const card = Array.from(root.querySelectorAll<HTMLElement>('[data-compare-card] input[type="checkbox"]')).find(
        focusable,
      );
      if (card) candidates.push(card);
    }
    candidates.sort((a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1));
    if (candidates[0]) candidates[0].focus();
    // Nothing on screen to point at (a field behind a switch that is off):
    // say what is wrong rather than doing nothing.
    else if (focusRequest.fallback) toast.error(focusRequest.fallback);
  }, [focusRequest]);

  const onInvalid = (errors: FieldErrors<CompareFormValues>) => {
    const first = firstMessage(errors);
    setFocusRequest((prev) => ({
      n: prev.n + 1,
      results: !!errors.results,
      fallback: first ? validationText(first) : t('compareSurfaces.checkHighlighted'),
    }));
  };

  const onValid = (v: CompareFormValues) => {
    // Volumes by chainage the server would refuse: point at the first station
    // field in error (each carries `aria-invalid`) rather than send the run.
    if (stationsOn && stationDerived.blocked) {
      setFocusRequest((prev) => ({ n: prev.n + 1, results: false, fallback: t('compareSurfaces.checkHighlighted') }));
      return;
    }
    // Re-read gates and flags now: a locked or hidden result, or a PDF that
    // cannot be made, is never sent — whatever the form still holds.
    const results = v.results.filter((r) => flags[r] && !locks[r] && availability[r].available);
    if (results.length === 0) {
      setFocusRequest((prev) => ({ n: prev.n + 1, results: true, fallback: t('compareSurfaces.results.noResult') }));
      return;
    }
    const cutFill = results.includes('cutFill');
    const submitted: CompareFormValues = {
      ...v,
      results,
      // Cut & fill add-ons left on without cut & fill are stripped, not an
      // error (ruling R20): the section shows them disabled and "Will
      // create" never promised them.
      ...(!cutFill && { stationVolumesEnabled: false, expectMode: 'NONE' as const }),
      addOns: { ...v.addOns, pdf: v.addOns.pdf && !pdfDisabledReason, dxf: v.addOns.dxf && cutFill },
    };
    const ctx: PayloadContext = {
      gates: {
        stationVolumes: stationGate.allowed,
        surfaceExpectation: expectationGate.allowed,
        reportPdf: reportGate.allowed,
      },
      layers: allLayers,
      stationDerived,
      minCoveragePct: initialStationParams?.minCoveragePct,
    };
    mutation.mutate({
      submission: submissionFor(submitted, ctx),
      values: submitted,
      edited: { tolerance: dirtyTolerance, deviationMode: !!dirtyFields.deviation?.mode },
    });
  };

  const sampling = (method: PcMethod) => ({
    // Sampling only feeds cut & fill (spec §4.2).
    visible: cutFillOn,
    label: t('compareSurfaces.sampling.label'),
    help: t('compareSurfaces.sampling.help'),
    notice:
      rerunNotice === 'legacyMinSampling' && method === 'min' ? t('compareSurfaces.sampling.legacyMin') : undefined,
  });

  const noteText = (() => {
    if (!resultsNote) return null;
    const result = translateKey(t, COMPARE_PART_LABEL_KEYS[resultsNote.result]);
    if (resultsNote.change === 'reticked') return translateKey(t, 'compareSurfaces.notes.reticked', { result });
    if (resultsNote.substitute) {
      const substitute = translateKey(t, COMPARE_PART_LABEL_KEYS[resultsNote.substitute]);
      return translateKey(t, 'compareSurfaces.notes.untickedSwitched', { result, substitute });
    }
    const key = resultsNote.reasonKey ? UNTICKED_NOTE_KEYS[resultsNote.reasonKey] : undefined;
    return key ? translateKey(t, key) : null;
  })();

  const primaryLabel = mutation.isPending
    ? t('compareSurfaces.starting')
    : ticked.length > 1
      ? t('compareSurfaces.runTwo')
      : t('compareSurfaces.run');

  if (sorted.length === 0) {
    return (
      <AnalysisDialogShell
        title={t('compareSurfaces.title')}
        description={t('compareSurfaces.description')}
        formId={formId}
        primaryLabel={t('compareSurfaces.run')}
        hidePrimary>
        <p data-testid='compare-empty' className='px-1 py-2 text-sm text-muted-foreground'>
          {t('compareSurfaces.empty')}
        </p>
      </AnalysisDialogShell>
    );
  }

  return (
    <Form {...form}>
      <FormMessageText value={validationText}>
        <AnalysisDialogShell
          title={t('compareSurfaces.title')}
          description={t('compareSurfaces.description')}
          formId={formId}
          primaryLabel={primaryLabel}
          // Volumes by chainage that cannot run say why in their section.
          // Run stays enabled (F3): a click starts nothing and takes the
          // user to the first field to fix, as an invalid submit does.
          primaryDisabled={mutation.isPending}
          primaryBusy={mutation.isPending}
          primaryTestId='compare-submit'
          // Sticky with the footer, next to "Run 2 analyses" — not lost at the
          // bottom of a long scrolling body.
          footerExtra={
            twoJobsLine && (
              <p
                role='note'
                data-testid='compare-two-jobs'
                className='min-w-0 text-xs text-pretty text-muted-foreground'>
                {translateKey(t, twoJobsLine.key, twoJobsLine.params)}
              </p>
            )
          }>
          <form ref={formRef} id={formId} onSubmit={form.handleSubmit(onValid, onInvalid)} className='min-w-0'>
            <div className='flex min-w-0 flex-col gap-4 py-1'>
              {(rerunNotice === 'stationsMovedToCutFill' || rerunNotice === 'expectationMovedToCutFill') && (
                <p
                  role='note'
                  data-testid='compare-rerun-notice'
                  className='rounded-md border border-amber-300/60 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200'>
                  {rerunNotice === 'stationsMovedToCutFill'
                    ? t('compareSurfaces.notices.stationsMovedToCutFill')
                    : t('compareSurfaces.notices.expectationMovedToCutFill')}
                </p>
              )}

              <AnalysisSection title={t('analysisConfig.sections.inputs')} testId='compare-section-inputs'>
                <CompareKindToggle value={kind} onChange={changeKind} />
                {/* Plain wrappers (focus target + testid): the picker's own
                  heading already names the role. */}
                <div ref={roleARef} data-testid='compare-role-a'>
                  <SurfacePicker
                    form={form}
                    prefix='inputs.top'
                    layers={sorted}
                    label={roleALabel}
                    sampling={sampling(topPcMethod)}
                  />
                </div>
                <SwapButton onSwap={swap} testId='compare-swap' />
                <div ref={roleBRef} data-testid='compare-role-b'>
                  <SurfacePicker
                    form={form}
                    prefix='inputs.bottom'
                    layers={sorted}
                    label={roleBLabel}
                    sampling={sampling(bottomPcMethod)}
                  />
                </div>
                <FormInputRoleField
                  control={form.control}
                  name='inputs.boundaryLayerId'
                  role='boundary'
                  optional
                  testId='compare-boundary'
                  layers={boundaryCandidates}
                  help={t('boundary.help')}
                />
                <OrientationSentence
                  kind={kind}
                  aName={design?.name ?? roleALabel}
                  bName={asBuilt?.name ?? roleBLabel}
                />
              </AnalysisSection>

              {showChoice && (
                <AnalysisSection title={t('compareSurfaces.results.heading')} testId='compare-section-results'>
                  <ResultCards
                    selected={results}
                    availability={availability}
                    locks={locks}
                    onToggle={toggleResult}
                    note={noteText}
                    showEmptyAlert={userEmptied || submitCount > 0}
                  />
                </AnalysisSection>
              )}

              <CompareSettings form={form} cutFillOn={cutFillOn} deviationOn={deviationOn} design={design} />

              <CompareAddOns
                form={form}
                cutFillOn={cutFillOn}
                deviationOn={deviationOn}
                cutFillOffered={availability.cutFill.visible && availability.cutFill.available}
                deviationOffered={availability.deviation.visible && availability.deviation.available}
                onAddResult={addResult}
                design={design}
                layers={sorted}
                alignments={alignments}
                stationValues={stationValues}
                stationDerived={stationDerived}
                onStationChange={applyStation}
                stationGate={stationGate}
                expectationGate={expectationGate}
                pdfDisabledReason={pdfDisabledReason}
                runName={runName}
              />

              <AnalysisSection title={t('analysisConfig.sections.output')} testId='compare-section-output'>
                <OutputSummary testId='compare-output' creates={creates} stacked footnote={createsFootnote}>
                  {outputLines.length > 0 && (
                    <ul data-testid='compare-preflight' className='flex min-w-0 flex-col gap-1 text-xs break-words'>
                      {outputLines.map((line) => (
                        <li
                          key={line.key}
                          data-testid='compare-preflight-line'
                          className={cn(
                            line.tone === 'warning' ? 'text-amber-700 dark:text-amber-400' : 'text-muted-foreground',
                          )}>
                          {translateKey(t, line.key, localizePreflightParams(line.params, i18n.language))}
                        </li>
                      ))}
                    </ul>
                  )}
                  <FormField
                    control={form.control}
                    name='output.name'
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('analysisConfig.resultName')}</FormLabel>
                        <FormControl>
                          <Input
                            data-testid='compare-name'
                            type='text'
                            {...field}
                            onChange={(e) => {
                              // Any keystroke owns the name, an emptying one
                              // too: refilling it mid clear-and-retype would
                              // fight the user's cursor.
                              setNameEdited(true);
                              field.onChange(e);
                            }}
                            onBlur={(e) => {
                              field.onBlur();
                              // Left empty: back to following the inputs.
                              if (e.target.value.trim() === '') setNameEdited(false);
                            }}
                          />
                        </FormControl>
                        <FormDescription className='text-xs'>
                          {cutFillOn && deviationOn
                            ? t('compareSurfaces.output.nameHelpPaired')
                            : t('deviationAnalysis.outputNameHelp')}
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </OutputSummary>
              </AnalysisSection>

              <CompareAdvanced
                form={form}
                cutFillOn={cutFillOn}
                deviationOn={deviationOn}
                design={design}
                rendersOn={rendersOn}
              />
            </div>
          </form>
        </AnalysisDialogShell>
      </FormMessageText>
    </Form>
  );
}
